// Navigator.js
// -----------------------------------------------------------------------------
// Shared top navigation chrome for NHV. Acts as a react-router layout route:
// it renders the sticky header, an <Outlet/> for the active page, and the
// shared <Footer/>.
//
// Responsive pattern (preserved):
//   - md+  : horizontal desktop nav with brand logo, text links, and a
//            prominent "Plan a Visit" CTA (PrimaryButton) + Give/Contact.
//   - xs   : compact bar with a hamburger that opens a right-anchored Drawer.
//
// Zero-trust notes:
//   - Nav destinations are a closed, in-file constant (NAV_LINKS): no external
//     input drives routing. Logo image is a bundled asset.
//   - The Drawer open state is component-local and the only mutable state here.
// -----------------------------------------------------------------------------

import * as React from 'react';
import { Outlet, Link } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';

import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

import Footer from './Footer';
import GiveModal from './GiveModal';
import { PrimaryButton } from './ui';
import logo from '../assets/vineyardLogo.png';
import './styles/navigator.css';

// Closed set of primary navigation destinations (label -> internal route).
// Kept as an in-file constant so routing is never driven by external input.
const NAV_LINKS = [
  { label: 'Who We Are', to: '/leaders' },
  { label: 'Beliefs', to: '/beliefs' },
  { label: 'Ministries', to: '/ministries' },
  { label: 'Sermons', to: '/sermons' },
];

const DRAWER_WIDTH = 288;

const Navigator = () => {
  // Component-local: drawer visibility is only meaningful to this component.
  const [open, setOpen] = React.useState(false);

  const openDrawer = () => setOpen(true);
  const closeDrawer = () => setOpen(false);

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      <Sheet
        component="header"
        variant="plain"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          // PNC-inspired: deep navy nav bar with light content on top of it.
          bgcolor: 'primary.500',
          color: 'common.white',
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.14)',
          boxShadow: 'xs',
        }}
      >
        {/* ----------------------------- Desktop (md+) ----------------------- */}
        <Box
          component="nav"
          aria-label="Primary"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            maxWidth: '1180px',
            mx: 'auto',
            width: '100%',
            px: { md: 5 },
            py: 1.25,
          }}
        >
          <Link to="/" className="nav-brand" aria-label="New Horizon Vineyard, home">
            <img className="nav-image" src={logo} alt="New Horizon Vineyard logo" />
          </Link>

          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center' }}
            component="ul"
            className="nav-list"
          >
            {NAV_LINKS.map((item) => (
              <li key={item.to}>
                <Link className="nav-link" to={item.to}>
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <Link className="nav-link" to="/contact">
                Contact
              </Link>
            </li>
            <li>
              <GiveModal linkClass="nav-link" text="Give" />
            </li>
          </Stack>

          <Box sx={{ flexShrink: 0 }}>
            <PrimaryButton to="/join" color="accent" size="md">
              Plan a Visit
            </PrimaryButton>
          </Box>
        </Box>

        {/* ----------------------------- Mobile (xs) ------------------------- */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 1,
          }}
        >
          <Link to="/" className="nav-brand" aria-label="New Horizon Vineyard, home">
            <img className="nav-image" src={logo} alt="New Horizon Vineyard logo" />
          </Link>

          <IconButton
            aria-label="Open navigation menu"
            aria-expanded={open}
            edge="end"
            onClick={openDrawer}
            sx={{ color: 'common.white' }}
          >
            <MenuIcon />
          </IconButton>

          <Drawer
            anchor="right"
            open={open}
            onClose={closeDrawer}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: DRAWER_WIDTH,
                // Match the navy nav bar so the drawer reads as the same chrome.
                // Material Drawer's sx resolves against the Material theme, not
                // Joy, so Joy token paths silently drop; use the Joy CSS vars.
                bgcolor: 'var(--joy-palette-primary-700, #12273f)',
                color: 'var(--joy-palette-common-white, #ffffff)',
                px: 2.5,
                py: 2,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pb: 1.5,
                mb: 1,
                borderBottom: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.14)',
              }}
            >
              <Typography level="title" sx={{ color: 'accent.300' }}>
                Menu
              </Typography>
              <IconButton
                aria-label="Close navigation menu"
                onClick={closeDrawer}
                sx={{ color: 'rgba(255, 255, 255, 0.85)' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              component="nav"
              aria-label="Primary mobile"
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              {NAV_LINKS.map((item) => (
                <Link
                  key={item.to}
                  className="nav-drawer-link"
                  to={item.to}
                  onClick={closeDrawer}
                >
                  {item.label}
                </Link>
              ))}
              {/* Same order as the desktop bar: ...Sermons, Contact, Give. */}
              <Link className="nav-drawer-link" to="/contact" onClick={closeDrawer}>
                Contact
              </Link>
              <GiveModal linkClass="nav-drawer-link" text="Give" />
            </Box>

            {/* Plan a Visit is the accent CTA on desktop; keep it as the
                pinned CTA here too. */}
            <Box sx={{ mt: 'auto', pt: 2.5 }}>
              <Link className="nav-drawer-give" to="/join" onClick={closeDrawer}>
                Plan a Visit
              </Link>
            </Box>
          </Drawer>
        </Box>
      </Sheet>

      <Outlet />
      <Footer />
    </Box>
  );
};

export default Navigator;
